import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Download,
  Flame,
  Loader2,
  Printer,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import {
  AdminAssessmentAnalytics,
  AdminAssessmentAttemptRow,
  AdminAssessmentSectionStat,
  AdminAssessmentWorkshop,
  AdminAttemptDetail,
  assessmentAnalyticsService,
  assessmentsService,
} from '../../services/api';

const fa = (n: number) => n.toLocaleString('fa-IR');

/** گزارش چاپی در تب جدا باز می‌شود تا ادمین لیست را از دست ندهد */
const openPrintReport = (attemptId: string) =>
  window.open(`/assessments/report/${attemptId}`, '_blank', 'noopener');
const MAX = 25;
/** اندازهٔ هر صفحه از فهرست پاسخ‌دهندگان؛ بقیه با «نمایش بیشتر» می‌آید. */
const PAGE_SIZE = 100;

/** همان پالت وضعیت که در سایت استفاده می‌شود */
const PRIORITY = {
  HIGH: { color: '#d03b3b', label: 'اولویت بالا', soft: 'bg-red-50 text-red-700 border-red-200' },
  MEDIUM: { color: '#fab219', label: 'متوسط', soft: 'bg-amber-50 text-amber-700 border-amber-200' },
  LOW: { color: '#0ca30c', label: 'پایین', soft: 'bg-green-50 text-green-700 border-green-200' },
} as const;

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

// ─── اجزای کوچک ──────────────────────────────────────────────────────────────

const Kpi: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'warn';
}> = ({ icon, label, value, hint, tone = 'default' }) => (
  <div
    className={`rounded-xl border bg-white p-4 ${
      tone === 'warn' ? 'border-amber-200' : 'border-gray-200'
    }`}
  >
    <div className="flex items-center gap-2 text-gray-400">
      {icon}
      <span className="text-xs font-bold">{label}</span>
    </div>
    <p className="mt-1.5 text-2xl font-black text-gray-900">{value}</p>
    {hint ? <p className="mt-0.5 text-[11px] font-bold text-gray-400">{hint}</p> : null}
  </div>
);

/**
 * نوار ترکیب اولویت یک بخش: سه قطعهٔ کنار هم که مجموعشان ۱۰۰٪ پاسخ‌دهندگان است.
 * برخلاف «میانگین» تنها، این نشان می‌دهد پراکندگی چطور است — مثلاً میانگین ۱۳
 * می‌تواند یعنی «همه متوسط» یا «نصف بحرانی، نصف بی‌مسئله»، که دو تصمیم متفاوت است.
 */
const PriorityMix: React.FC<{ stat: AdminAssessmentSectionStat }> = ({ stat }) => {
  const total = stat.responses || 1;
  const parts = [
    { key: 'HIGH' as const, count: stat.highCount },
    { key: 'MEDIUM' as const, count: stat.mediumCount },
    { key: 'LOW' as const, count: stat.lowCount },
  ];

  return (
    <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
      {parts.map((part) =>
        part.count ? (
          <div
            key={part.key}
            title={`${PRIORITY[part.key].label}: ${fa(part.count)} نفر`}
            style={{
              width: `${(part.count / total) * 100}%`,
              backgroundColor: PRIORITY[part.key].color,
            }}
          />
        ) : null,
      )}
    </div>
  );
};

/** بازهٔ کمینه تا بیشینه با نشانگر میانگین روی محور ۰ تا ۲۵ */
const RangeBar: React.FC<{ stat: AdminAssessmentSectionStat }> = ({ stat }) => {
  const pct = (v: number) => (v / MAX) * 100;

  return (
    <div className="relative h-5">
      <span className="absolute inset-y-0 right-0 my-auto h-px w-full bg-gray-100" />
      <span
        className="absolute inset-y-0 my-auto h-1.5 rounded-full bg-gray-200"
        style={{ right: `${pct(stat.min)}%`, width: `${pct(stat.max - stat.min)}%` }}
        title={`دامنه: ${fa(stat.min)} تا ${fa(stat.max)}`}
      />
      <span
        className="absolute inset-y-0 my-auto h-3.5 w-3.5 rounded-full border-2 border-white shadow"
        style={{
          right: `${pct(stat.average)}%`,
          transform: 'translateX(50%)',
          backgroundColor:
            stat.average <= 8 ? PRIORITY.HIGH.color : stat.average <= 16 ? PRIORITY.MEDIUM.color : PRIORITY.LOW.color,
        }}
        title={`میانگین: ${fa(stat.average)}`}
      />
    </div>
  );
};

// ─── تب تحلیل ────────────────────────────────────────────────────────────────

interface Props {
  assessmentId: string;
  workshops: AdminAssessmentWorkshop[];
}

export const AssessmentAnalytics: React.FC<Props> = ({ assessmentId, workshops }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workshopId, setWorkshopId] = useState('');
  const [analytics, setAnalytics] = useState<AdminAssessmentAnalytics | null>(null);
  const [attempts, setAttempts] = useState<AdminAssessmentAttemptRow[]>([]);
  const [attemptsTotal, setAttemptsTotal] = useState(0);
  const [attemptsLoading, setAttemptsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState<AdminAttemptDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    setError('');
    try {
      setAnalytics(await assessmentAnalyticsService.get(assessmentId, workshopId || undefined));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'دریافت گزارش انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, workshopId]);

  useEffect(() => {
    load();
  }, [load]);

  // جستجو سمت سرور است تا فراتر از صفحهٔ بارگذاری‌شده هم پیدا شود
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchAttempts = useCallback(
    (skip: number) =>
      assessmentsService.listAttempts(assessmentId, {
        workshopId: workshopId || undefined,
        search: query || undefined,
        take: PAGE_SIZE,
        skip,
      }),
    [assessmentId, workshopId, query],
  );

  useEffect(() => {
    if (!assessmentId) return undefined;
    let cancelled = false;
    setAttemptsLoading(true);
    fetchAttempts(0)
      .then((data) => {
        if (cancelled) return;
        setAttempts(data.attempts);
        setAttemptsTotal(data.total);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || 'دریافت پاسخ‌دهندگان انجام نشد.');
      })
      .finally(() => {
        if (!cancelled) setAttemptsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assessmentId, fetchAttempts]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchAttempts(attempts.length);
      setAttempts((prev) => [...prev, ...data.attempts]);
      setAttemptsTotal(data.total);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'دریافت ادامهٔ فهرست انجام نشد.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const blob = await assessmentAnalyticsService.exportExcel(assessmentId, workshopId || undefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `گزارش-پرسشنامه-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'دریافت خروجی اکسل انجام نشد.');
    } finally {
      setExporting(false);
    }
  };

  const openDetail = async (attemptId: string) => {
    setDetailLoading(true);
    try {
      setDetail(await assessmentAnalyticsService.attemptDetail(attemptId));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'دریافت جزئیات انجام نشد.');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-16 text-sm font-bold text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        در حال محاسبهٔ گزارش…
      </div>
    );
  }

  if (!analytics) return null;

  const { kpis, sectionStats, courseDemand } = analytics;
  const maxDemand = courseDemand[0]?.topThree || 1;
  // در حالت تحلیل، «دوره» معنایی ندارد؛ KPI و کارت‌های مربوط به دوره پنهان می‌شوند
  const isAnalysis = analytics.resultMode === 'ANALYSIS';

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <label className="text-sm font-bold text-gray-600">تفکیک کارگاه:</label>
        <select
          value={workshopId}
          onChange={(e) => setWorkshopId(e.target.value)}
          className={`${inputClass} w-auto min-w-[200px]`}
        >
          <option value="">همه</option>
          {workshops.map((w) => (
            <option key={w.id} value={w.id}>
              {w.title}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || !kpis.totalCompleted}
          className="mr-auto flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          خروجی اکسل
        </button>
      </div>

      {/* KPI */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          icon={<CheckCircle2 className="h-4 w-4" />}
          label="پاسخ تکمیل‌شده"
          value={fa(kpis.totalCompleted)}
          hint={[
            kpis.uniqueRespondents !== kpis.totalCompleted
              ? `${fa(kpis.uniqueRespondents)} نفر یکتا`
              : null,
            kpis.inProgress ? `${fa(kpis.inProgress)} نیمه‌کاره` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        <Kpi
          icon={<Users className="h-4 w-4" />}
          label="ظرفیت دعوت"
          value={kpis.invitedCount ? fa(kpis.invitedCount) : '—'}
          hint={
            kpis.completionRate !== null
              ? `نرخ تکمیل ${fa(kpis.completionRate)}٪`
              : kpis.invitedCount
                ? 'بیشتر پاسخ‌ها بدون کد دعوت آمده‌اند'
                : 'بدون دعوت‌نامه'
          }
        />
        <Kpi
          icon={<Flame className="h-4 w-4" />}
          label={isAnalysis ? 'شدیدترین محور' : 'فوری‌ترین محور'}
          value={kpis.mostUrgent ?? '—'}
        />
        {!workshopId && kpis.withoutWorkshopCount ? (
          <Kpi
            icon={<AlertTriangle className="h-4 w-4" />}
            label="پاسخ بدون کارگاه"
            value={fa(kpis.withoutWorkshopCount)}
            hint="به نام هیچ کارگاهی ثبت نشده؛ در فیلتر کارگاه‌ها دیده نمی‌شود"
            tone="warn"
          />
        ) : null}
        {!isAnalysis ? (
          <Kpi
            icon={<AlertTriangle className="h-4 w-4" />}
            label="بخش بدون دوره"
            value={fa(kpis.sectionsWithoutCourse)}
            hint={kpis.sectionsWithoutCourse ? 'در نتیجه دوره‌ای پیشنهاد نمی‌شود' : 'همه وصل‌اند'}
            tone={kpis.sectionsWithoutCourse ? 'warn' : 'default'}
          />
        ) : null}
      </div>

      {!kpis.totalCompleted ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-sm font-bold text-gray-400">
          هنوز پاسخ تکمیل‌شده‌ای ثبت نشده است.
        </div>
      ) : (
        <>
          {/* تقاضای دوره */}
          {courseDemand.length ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="mb-1 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                <p className="text-sm font-bold text-gray-800">تقاضای دوره‌ها</p>
              </div>
              <p className="mb-4 text-[11.5px] font-bold text-gray-400">
                چند بار هر دوره در سه اولویت اولِ یک پاسخ‌دهنده آمده است — یعنی چه چیزی را
                باید بیشتر تبلیغ یا تولید کنید.
              </p>
              <div className="space-y-2">
                {courseDemand.slice(0, 10).map((course, index) => (
                  <div key={course.id} className="flex items-center gap-3">
                    <span className="w-5 shrink-0 text-center text-[11px] font-black text-gray-300">
                      {fa(index + 1)}
                    </span>
                    <span className="w-48 shrink-0 truncate text-xs font-bold text-gray-700">
                      {course.title}
                    </span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-blue-600 transition-all duration-500"
                        style={{ width: `${(course.topThree / maxDemand) * 100}%` }}
                      />
                    </div>
                    <span className="w-24 shrink-0 text-left text-[11px] font-black text-gray-700">
                      {fa(course.topThree)} بار
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* آمار بخش‌ها */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-1 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              <p className="text-sm font-bold text-gray-800">وضعیت هر محور</p>
            </div>
            <p className="mb-4 text-[11.5px] font-bold text-gray-400">
              مرتب از فوری‌ترین. ستون «دامنه» میانگین را روی بازهٔ کمینه تا بیشینه نشان
              می‌دهد و ستون «ترکیب» می‌گوید چند درصد پاسخ‌دهندگان در هر سطح اولویت‌اند.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-right text-sm">
                <thead className="border-b border-gray-100 text-[11px] font-bold text-gray-400">
                  <tr>
                    <th className="px-2 pb-2">محور</th>
                    <th className="px-2 pb-2">دورهٔ متصل</th>
                    <th className="w-16 px-2 pb-2">پاسخ</th>
                    <th className="w-40 px-2 pb-2">دامنه (۰ تا ۲۵)</th>
                    <th className="w-16 px-2 pb-2">میانگین</th>
                    <th className="w-16 px-2 pb-2">میانه</th>
                    <th className="w-36 px-2 pb-2">ترکیب اولویت</th>
                    <th className="w-24 px-2 pb-2">در ۳ اولویت اول</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sectionStats.map((stat) => (
                    <tr
                      key={stat.sectionId}
                      className={`hover:bg-gray-50 ${stat.responses ? '' : 'opacity-50'}`}
                    >
                      <td className="px-2 py-2.5 text-xs font-bold text-gray-800">{stat.title}</td>
                      <td className="px-2 py-2.5 text-[11px] text-gray-500">
                        {stat.course ? (
                          <span className="line-clamp-1">{stat.course.title}</span>
                        ) : (
                          <span className="rounded bg-amber-50 px-1.5 py-0.5 font-bold text-amber-700">
                            بدون دوره
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-xs font-bold tabular-nums text-gray-500">
                        {stat.responses ? fa(stat.responses) : '—'}
                      </td>
                      <td className="px-2 py-2.5">
                        {stat.responses ? <RangeBar stat={stat} /> : null}
                      </td>
                      <td className="px-2 py-2.5 text-xs font-black tabular-nums text-gray-900">
                        {stat.responses ? fa(stat.average) : '—'}
                      </td>
                      <td className="px-2 py-2.5 text-xs font-bold tabular-nums text-gray-500">
                        {stat.responses ? fa(stat.median) : '—'}
                      </td>
                      <td className="px-2 py-2.5">
                        {stat.responses ? (
                          <>
                            <PriorityMix stat={stat} />
                            <p className="mt-1 text-[10px] font-bold text-gray-400">
                              {fa(stat.highCount)} / {fa(stat.mediumCount)} / {fa(stat.lowCount)}
                            </p>
                          </>
                        ) : (
                          <span className="text-[10px] font-bold text-gray-400">بدون پاسخ</span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-xs font-black tabular-nums text-blue-700">
                        {stat.responses ? fa(stat.topThreeCount) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-2.5 text-[10.5px] font-bold text-gray-400">
              {(['HIGH', 'MEDIUM', 'LOW'] as const).map((key) => (
                <span key={key} className="inline-flex items-center gap-1">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: PRIORITY[key].color }}
                  />
                  {PRIORITY[key].label}
                </span>
              ))}
            </div>
          </div>

          {/* پاسخ‌دهندگان */}
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 p-4">
              <p className="text-sm font-bold text-gray-800">
                پاسخ‌دهندگان ({fa(attemptsTotal)})
                {attempts.length < attemptsTotal ? (
                  <span className="mr-2 text-[11px] font-bold text-gray-400">
                    نمایش {fa(attempts.length)} مورد
                  </span>
                ) : null}
              </p>
              {attemptsLoading ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : null}
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="جستجوی نام، موبایل یا ایمیل…"
                className={`${inputClass} mr-auto w-auto min-w-[220px]`}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-right text-sm">
                <thead className="bg-gray-50 text-[11px] font-bold text-gray-500">
                  <tr>
                    <th className="px-4 py-3">کاربر</th>
                    <th className="px-4 py-3">موبایل</th>
                    <th className="px-4 py-3">کارگاه</th>
                    <th className="px-4 py-3">سه اولویت اول</th>
                    <th className="px-4 py-3">تاریخ</th>
                    <th className="px-4 py-3 text-center">گزارش</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {attempts.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => openDetail(row.id)}
                      className="cursor-pointer hover:bg-blue-50/40"
                    >
                      <td className="px-4 py-3 font-bold text-gray-800">
                        {[row.user.firstName, row.user.lastName].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{row.user.phone || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{row.workshopTitle || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {row.topPriorities.map((p) => (
                            <span
                              key={p.title}
                              className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700"
                            >
                              {p.title} ({fa(p.score25)})
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {row.completedAt
                          ? new Date(row.completedAt).toLocaleDateString('fa-IR')
                          : '—'}
                      </td>
                      <td className="px-2 py-3 text-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPrintReport(row.id);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[11px] font-bold text-gray-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                          title="چاپ گزارش تحلیلی"
                        >
                          <Printer className="h-3.5 w-3.5" />
                          چاپ
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!attempts.length && !attemptsLoading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center font-bold text-gray-400">
                        موردی یافت نشد.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {attempts.length < attemptsTotal ? (
              <div className="border-t border-gray-100 p-3 text-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                >
                  {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  نمایش بیشتر ({fa(attemptsTotal - attempts.length)} مورد باقی‌مانده)
                </button>
              </div>
            ) : null}
          </div>
        </>
      )}

      {/* جزئیات یک پاسخ‌دهنده */}
      {detail || detailLoading ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDetail(null)}
        >
          <div
            className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5"
            onClick={(e) => e.stopPropagation()}
          >
            {detailLoading || !detail ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm font-bold text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                در حال بارگذاری…
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-base font-bold text-gray-900">
                      {[detail.user.firstName, detail.user.lastName].filter(Boolean).join(' ') ||
                        'بدون نام'}
                    </p>
                    <p className="mt-0.5 text-xs font-bold text-gray-500">
                      {detail.user.phone || '—'}
                      {detail.workshop ? ` · ${detail.workshop.title}` : ''}
                      {detail.completedAt
                        ? ` · ${new Date(detail.completedAt).toLocaleDateString('fa-IR')}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openPrintReport(detail.attemptId)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-blue-700"
                    >
                      <Printer className="h-3.5 w-3.5" />
                      گزارش چاپی
                    </button>
                    <button
                      type="button"
                      onClick={() => setDetail(null)}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
                      aria-label="بستن"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  {detail.items.map((item) => (
                    <div
                      key={item.sectionId}
                      className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2"
                    >
                      <span className="w-6 shrink-0 text-center text-[11px] font-black text-gray-300">
                        {fa(item.rank)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-bold text-gray-800">{item.title}</p>
                        {item.course ? (
                          <p className="truncate text-[10.5px] font-bold text-gray-400">
                            {item.course.title}
                          </p>
                        ) : null}
                      </div>
                      <div className="h-2 w-28 shrink-0 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(item.score25 / MAX) * 100}%`,
                            backgroundColor: PRIORITY[item.priority].color,
                          }}
                        />
                      </div>
                      <span
                        className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-black ${PRIORITY[item.priority].soft}`}
                      >
                        {fa(item.score25)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
